import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { AdminRevenueStatistics } from "../../redux/features/payment/payment.types";
import { FrequencyFilter } from "../../utils/types";
import {
  formatDate,
  formatToCurrency,
  normalizeDate,
} from "../../utils/helpers";
import { TabNav } from "../tabNav";
import { currencyShorts } from "../../utils/constants";

export const RevenueStatsChart = ({
  fetchingRevenueStats,
  revenueStats,
  frequency,
  setFrequency,
}: {
  fetchingRevenueStats: boolean;
  revenueStats?: AdminRevenueStatistics;
  frequency: FrequencyFilter;
  setFrequency: (frequency: FrequencyFilter) => void;
}) => {
  const tabs = [
    {
      title: "Days",
      to: "daily",
      onClick: () => {
        setFrequency("daily");
      },
    },
    {
      title: "Weeks",
      to: "weekly",
      onClick: () => {
        setFrequency("weekly");
      },
    },
    {
      title: "Months",
      to: "monthly",
      onClick: () => {
        setFrequency("monthly");
      },
    },
  ];
  return (
    <div className="w-full">
      <div className="flex flex-col gap-16 rounded-[14px] border border-grey-200 p-5">
        <div className="flex flex-wrap justify-between gap-8">
          <div className="flex flex-1 flex-col gap-2">
            <p className="text-base text-grey-600">Total Tikket Profit</p>
            {!fetchingRevenueStats && (
              <p className="text-2xl font-extrabold">
                {formatToCurrency(revenueStats?.data?.totalTicketProfit ?? 0, {
                  currency: currencyShorts["NGN"],
                })}
              </p>
            )}
          </div>

          <TabNav
            navs={tabs}
            variant="switcher"
            activeTab={frequency}
            theme="plain"
            className="h-fit"
          />
        </div>

        <ResponsiveContainer width={"100%"} height={204}>
          <AreaChart
            data={
              fetchingRevenueStats
                ? []
                : revenueStats?.data?.revenueGraph?.map((entry) => {
                    return {
                      dateLabel: entry.label,
                      label:
                        frequency === "daily"
                          ? formatDate(entry.label, "MMM DD")
                          : frequency === "monthly"
                            ? formatDate(
                                `${entry.label}-01`,
                                "MMM",
                                false,
                                true,
                              )
                            : normalizeDate(entry.label),
                      revenue: entry.revenue,
                    };
                  })
            }
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="lineColor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#98C8F7" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#98C8F7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 0"
              vertical={false}
              stroke="#E7ECF2"
            />
            <XAxis
              dataKey="label"
              className="!font-fustat !text-sm !text-grey-600"
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={({ payload }) => {
                if (!payload?.length) return null;
                const dateLabel = payload[0]?.payload?.dateLabel;
                const formattedLabel =
                  frequency === "daily"
                    ? formatDate(dateLabel, "MMM DD")
                    : frequency === "monthly"
                      ? formatDate(`${dateLabel}-01`, "MMM YYYY", false, true)
                      : frequency === "weekly"
                        ? normalizeDate(dateLabel)
                        : "";
                return (
                  <div className="flex flex-col gap-2 rounded-[4px] border border-grey-100 bg-white px-3 py-[6px] text-center text-sm shadow-md">
                    <p className="font-bold text-grey-800">
                      {payload
                        ? `${formatToCurrency(Number(payload[0]?.value) ?? 0, {})} Profit`
                        : ""}
                    </p>
                    <p className="text-grey-600">{formattedLabel}</p>
                  </div>
                );
              }}
              cursor={{
                stroke: "#1976D2",
                strokeWidth: 2,
                strokeDasharray: "8 8",
              }}
            />
            <Area
              type="linear"
              dataKey="revenue"
              stroke="#1976D2"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#lineColor)"
              activeDot={{
                r: 6,
                stroke: "#1976D2",
                fill: "white",
                strokeWidth: 2,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
